
import { CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { format } from "date-fns";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { MissionActions } from "./MissionActions";
import { MissionDetails } from "./MissionDetails";
import { useUserRoles } from "@/hooks/useUserRoles";
import { cn } from "@/lib/utils";

interface Mission {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: string | null;
  team_id: string;
  created_at: string;
  start_date: string | null;
  end_date: string | null;
  attachment_url: string | null;
  team_name?: string;
  team_image_url?: string; // Add team_image_url property
}

interface MissionCardProps {
  mission: Mission;
  isAdmin: boolean;
  onDelete: () => void;
  userRole?: string;
}

export const MissionCard = ({ mission, isAdmin, userRole, onDelete }: MissionCardProps) => {
  const createdDate = new Date(mission.created_at);
  const { roles } = useUserRoles();
  const isClient = roles.includes('client');
  const canModifyMission = isAdmin || isClient;

  // Generate subtle variations based on mission ID for visual diversity
  const missionVariation = mission.id.charCodeAt(0) % 6;
  
  const getGradientVariation = () => {
    switch (missionVariation) {
      case 0:
        return "from-blue-50/80 to-blue-100/60"; // Soft blue
      case 1:
        return "from-purple-50/80 to-purple-100/60"; // Soft purple
      case 2:
        return "from-emerald-50/80 to-emerald-100/60"; // Soft emerald
      case 3:
        return "from-orange-50/80 to-orange-100/60"; // Soft orange
      case 4:
        return "from-pink-50/80 to-pink-100/60"; // Soft pink
      case 5:
        return "from-teal-50/80 to-teal-100/60"; // Soft teal
      default:
        return "from-blue-50/80 to-blue-100/60";
    }
  };

  const getBorderColor = () => {
    switch (missionVariation) {
      case 0:
        return "border-blue-200/40";
      case 1:
        return "border-purple-200/40";
      case 2:
        return "border-emerald-200/40";
      case 3:
        return "border-orange-200/40";
      case 4:
        return "border-pink-200/40";
      case 5:
        return "border-teal-200/40";
      default:
        return "border-blue-200/40";
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Card className={cn(
          "relative overflow-hidden group transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg rounded-xl border cursor-pointer min-h-[200px] flex flex-col",
          `bg-gradient-to-br ${getGradientVariation()}`,
          getBorderColor()
        )}>
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-8 h-8 bg-white rounded-full -translate-y-4 translate-x-4"></div>
            <div className="absolute bottom-0 left-0 w-6 h-6 bg-white rounded-full translate-y-3 -translate-x-3"></div>
          </div>
          
          {/* Subtle shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
          
          <CardHeader className="relative">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/25 backdrop-blur-sm border border-white/40 flex items-center justify-center overflow-hidden">
                  {mission.team_image_url ? (
                    <img 
                      src={mission.team_image_url} 
                      alt={`${mission.team_name || 'Team'} logo`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                      {mission.team_name ? mission.team_name.charAt(0).toUpperCase() : 'T'}
                    </div>
                  )}
                </div>
                <h3 className="font-semibold truncate text-slate-700 group-hover:text-slate-600 transition-colors">{mission.title}</h3>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative text-sm text-slate-600/80 space-y-2 flex-grow flex flex-col justify-between">
            <div className="space-y-2">
              <p className="truncate">Category: {mission.category}</p>
              {mission.team_name && (
                <p className="truncate text-xs text-slate-500">Team: {mission.team_name}</p>
              )}
              <p className="text-xs text-slate-500">
                Created: {format(createdDate, "MMM d, yyyy")}
              </p>
              {mission.description && (
                <p className="line-clamp-2">
                  {mission.description}
                </p>
              )}
              {mission.attachment_url && (
                <div className="mt-3 rounded-lg overflow-hidden border border-white/40 bg-white/30">
                  <img 
                    src={mission.attachment_url} 
                    alt={mission.title}
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      console.error('Failed to load mission image:', mission.attachment_url);
                      e.currentTarget.style.display = 'none';
                    }}
                    onLoad={() => console.log('Mission card image loaded:', mission.attachment_url)}
                  />
                </div>
              )}
            </div>
            {canModifyMission && (
              <div className="pt-4 w-full">
                <MissionActions 
                  missionId={mission.id}
                  onDelete={onDelete}
                  canModifyMission={canModifyMission}
                  isCompleted={mission.status === 'completed'}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </SheetTrigger>
      <MissionDetails mission={mission} />
    </Sheet>
  );
};
